import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ChevronsUpDown, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import {
  COUNTRY_CODES, DEFAULT_COUNTRY, formatE164, isValidPhone,
} from "../lib/countryCodes";

/**
 * Modal BLOQUEANTE que se muestra cuando un usuario autenticado no tiene
 * teléfono cargado (típicamente porque entró vía Google OAuth y Google no
 * nos compartió el número). Sin teléfono no podemos asignar conductor ni
 * avisar por WhatsApp, así que forzamos el dato antes de dejarlo navegar.
 *
 * El diálogo NO tiene botón de cerrar ni cierra al click fuera. Sólo se
 * cierra cuando el usuario guarda un teléfono válido (E.164).
 *
 * Renderizado vía `createPortal` al `body` para garantizar que quede
 * encima de cualquier contenido, incluidos BottomSheets.
 */
export default function RequirePhoneDialog() {
  const { user, refresh } = useAuth();
  const needsPhone = useMemo(
    // Solo bloqueamos a clientes. Admin/super_admin no necesitan teléfono
    // aquí — no reciben pedidos y su contacto se gestiona por otros medios.
    () => Boolean(user) && user?.role === "client" && !String(user?.phone || "").trim(),
    [user?.user_id, user?.phone, user?.role],
  );

  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countryOpen, setCountryOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  if (!needsPhone) return null;

  const valid = isValidPhone(country, phone);

  const save = async () => {
    if (!valid) {
      toast.error("Teléfono inválido");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/me", { phone: formatE164(country, phone) });
      await refresh();
      toast.success("Teléfono guardado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error guardando teléfono");
    } finally {
      setSaving(false);
    }
  };

  const node = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="require-phone-backdrop"
      // Capturamos y bloqueamos click en el backdrop: no se cierra.
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl border border-neutral-200 shadow-2xl p-6 sm:p-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="require-phone-title"
        data-testid="require-phone-dialog"
      >
        <div className="flex items-center justify-center h-12 w-12 rounded-full bg-neutral-100 mb-4">
          <Phone size={22} className="text-neutral-700" />
        </div>
        <h2 id="require-phone-title" className="font-display text-2xl font-extrabold tracking-tight">
          Agrega tu teléfono
        </h2>
        <p className="text-sm text-neutral-500 mt-1">
          Lo necesitamos para confirmar tu pedido, avisarte por WhatsApp y que la unidad de reparto pueda contactarte.
        </p>

        <div className="mt-5">
          <Label>Teléfono</Label>
          <div className="flex gap-2 mt-1">
            <Popover open={countryOpen} onOpenChange={setCountryOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={countryOpen}
                  className="h-12 px-3 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 flex items-center gap-1.5 shrink-0 text-sm font-medium outline-none focus:ring-2 focus:ring-offset-1 focus:ring-neutral-300"
                  data-testid="require-phone-country-trigger"
                >
                  <span className="text-lg leading-none">{country.flag}</span>
                  <span className="tabular-nums">{country.dial}</span>
                  <ChevronsUpDown size={14} className="text-neutral-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[280px]" align="start">
                <Command>
                  <CommandInput placeholder="Buscar país…" />
                  <CommandList>
                    <CommandEmpty>Sin resultados.</CommandEmpty>
                    <CommandGroup>
                      {COUNTRY_CODES.map((c) => (
                        <CommandItem
                          key={c.code}
                          value={`${c.name} ${c.dial} ${c.code}`}
                          onSelect={() => { setCountry(c); setCountryOpen(false); }}
                          data-testid={`require-phone-country-${c.code}`}
                        >
                          <span className="text-lg mr-2">{c.flag}</span>
                          <span className="flex-1">{c.name}</span>
                          <span className="text-xs text-neutral-500 tabular-nums mr-2">{c.dial}</span>
                          {country.code === c.code && <Check size={14} />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="10 dígitos"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 15))}
              className="h-12 flex-1"
              autoFocus
              data-testid="require-phone-input"
            />
          </div>
          {phone && !valid && (
            <p className="mt-1 text-xs text-red-600" data-testid="require-phone-error">
              Formato inválido para el país seleccionado.
            </p>
          )}
        </div>

        <Button
          onClick={save}
          disabled={!valid || saving}
          className="w-full h-12 mt-5 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 rounded-xl text-base font-semibold"
          data-testid="require-phone-save-btn"
        >
          {saving ? "Guardando..." : "Guardar y continuar"}
        </Button>

        <p className="text-[11px] text-neutral-400 text-center mt-3">
          No podrás usar la app hasta agregar un teléfono válido.
        </p>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}
